/* @flow */
import React from 'react';
import { Layout } from 'antd';
import { ContainerQuery } from 'react-container-query';
import classNames from 'classnames';
import { RouterProps } from 'react-router';
import Media from 'react-media';
import { Sidebar } from '@/components';
import { QUERY } from '@/constants/Responsive';
import logo from '@/assets/company_logo_icon.png';
import settings from '@/config/defaultConfig';
import Footer from '../FooterLayout';
import Header from '../HeaderLayout';
import Context from '@/context/LayoutContext';
import styles from './BaseLayout.module.pcss';

type Props = {
  router: RouterProps,
  menuItems: Array<MenuItemsT>,
  isMobile: boolean,
  isCollapsed: boolean,
  changeLayoutCollapsed: ({ isCollapsed: boolean }) => void,
  children: React$Node,
};

const { Content } = Layout;

function BaseLayout(props: Props) {
  const { children, isMobile, menuItems } = props;

  const getContext = () => {
    const { router, isMobile } = props;
    return {
      isMobile,
      location: router.location,
    };
  };

  const getLayoutStyle = () => {
    const { fixedSidebar, layout, siderWidth } = settings;
    const { isMobile, isCollapsed } = props;
    if (fixedSidebar && layout !== 'topmenu' && !isMobile) {
      return {
        paddingLeft: isCollapsed ? '100px' : siderWidth,
      };
    }
    return null;
  };

  const handleMenuCollapse = (isCollapsed: boolean) => {
    const { changeLayoutCollapsed } = props;
    changeLayoutCollapsed({ isCollapsed: isCollapsed });
  };

  const { layout: PropsLayout, fixedHeader, siderWidth } = settings;

  const isTop = PropsLayout === 'topmenu';
  const contentStyle = fixedHeader ? { paddingTop: 64 } : { paddingTop: 0 };
  const layout = (
    <Layout data-cy="base-layout">
      {isTop && !isMobile ? null : (
        <Sidebar
          {...props}
          logo={logo}
          onCollapse={handleMenuCollapse}
          menuData={menuItems}
          settings={settings}
          isMobile={isMobile}
          siderWidth={siderWidth}
        />
      )}
      <Layout
        style={{
          ...getLayoutStyle(),
          minHeight: '100vh',
        }}
      >
        <Header
          {...props}
          handleMenuCollapse={handleMenuCollapse}
          logo={logo}
          isMobile={isMobile}
          settings={settings}
        />
        <Content className={styles.content} style={contentStyle} data-cy="base-layout-content">
          {children}
        </Content>
        <Footer />
      </Layout>
    </Layout>
  );

  return (
    <>
      <ContainerQuery query={QUERY}>
        {(params) => (
          <Context.Provider value={getContext()}>
            <div className={classNames(params)}>{layout}</div>
          </Context.Provider>
        )}
      </ContainerQuery>
    </>
  );
}

export default (props: Props) => (
  <Media query="(max-width: 599px)">
    {(isMobile) => (
      <BaseLayout {...props} isMobile={isMobile}>
        {props.children}
      </BaseLayout>
    )}
  </Media>
);
